import type {
  SmsUsageResponse,
  SmsReportResponse,
  SmsGraphResponse,
  SmsReportQueryParams,
  SmsGraphRequestBody,
} from 'src/types/sms-report';

import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

export interface ChannelListType {
  channelName: string;
  channelType: string;
  configId: string;
  createdAt: string;
  id: string;
  teamId: string | null;
  tenantId: string;
  updatedAt: string;
}

const CHANNEL_API = {
  list: 'api/channels/list',
  add: 'api/channels',
  update: 'api/channels',
  details: 'api/channels/details',
  delete: 'api/channels',
  assign: 'channel/assignchannel',

  msSync: 'channel/ms-sync',
  sendMail: 'api/channels/sendMail',
};

export const channelApi = createApi({
  reducerPath: 'channelApi',
  baseQuery,
  endpoints: (builder) => ({
    // ----------------------------------------------------------------------
    // NOTE FOR DEVELOPERS:
    // Please maintain the following order for endpoints ensuring CRUD consistency:
    // 1. List (Filter)
    // 2. Details
    // 3. Create
    // 4. Update
    // 5. Delete
    // ----------------------------------------------------------------------

    filterChannel: builder.mutation({
      query: (filterQuery) => ({
        url: CHANNEL_API.list,
        method: 'POST',
        body: Object.keys(filterQuery).length > 0 ? filterQuery : { filters: [] },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { list?: ChannelListType[]; total: number } }) => ({
        list: response?.data?.list ?? [],
        total: response?.data?.total ?? 0,
      }),
    }),

    getChannelList: builder.mutation({
      query: (channelPayload) => ({
        url: CHANNEL_API.list,
        method: 'POST',
        body: Object.keys(channelPayload).length > 0 ? channelPayload : { filters: [] },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { list?: ChannelListType[]; total: number } }) => ({
        list: response?.data?.list ?? [],
        total: response?.data?.total ?? 0,
      }),
    }),

    getChannelDtl: builder.mutation({
      query: (id: string) => ({
        url: `${CHANNEL_API.details}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: any }) => ({
        data: { channel: response.data },
      }),
    }),

    getChannelDeatil: builder.mutation({
      query: (id: string) => ({
        url: CHANNEL_API.details,
        method: 'POST',
        body: { id },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    createChannel: builder.mutation({
      query: (newChannel) => ({
        url: CHANNEL_API.add,
        method: 'POST',
        body: newChannel,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    updateChannel: builder.mutation({
      query: ({ id, updates }) => ({
        url: `${CHANNEL_API.update}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    deleteChannels: builder.mutation({
      query: (id: string) => ({
        url: `${CHANNEL_API.delete}/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    sendIVRRequest: builder.mutation({
      query: (ivrPayload) => ({
        url: CHANNEL_API.sendMail,
        method: 'POST',
        body: ivrPayload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    sendDIDRequest: builder.mutation({
      query: (didPayload) => ({
        url: CHANNEL_API.sendMail,
        method: 'POST',
        body: didPayload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    assignChannel: builder.mutation({
      query: (payload) => ({
        url: CHANNEL_API.assign,
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    syncMiscrosoftTeamsContact: builder.mutation({
      query: (payload) => ({
        url: CHANNEL_API.msSync,
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    getBmsBalance: builder.query<
      { success: boolean; data: { balance: number; raw: string } },
      void
    >({
      query: () => ({
        url: 'api/sms/bms/balance',
        method: 'GET',
      }),
    }),

    getBmsStatus: builder.query<
      { success: boolean; data: { respid: string; rawStatus: string; status: string } },
      string
    >({
      query: (respid) => ({
        url: `api/sms/bms/status/${respid}`,
        method: 'GET',
      }),
    }),

    getBmsCoverage: builder.query<
      { success: boolean; data: { format: number; data: string } },
      number
    >({
      query: (format) => ({
        url: 'api/sms/bms/coverage',
        params: { format },
        method: 'GET',
      }),
    }),

    // SMS Usage & Report (Tenant)
    getSmsUsage: builder.query<SmsUsageResponse, void>({
      query: () => ({
        url: 'api/sms/usage',
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),

    getSmsReport: builder.query<SmsReportResponse, SmsReportQueryParams | void>({
      query: (params) => ({
        url: 'api/sms/report',
        method: 'GET',
        params: params || {},
      }),
      keepUnusedDataFor: 0,
    }),

    getSmsReportGraph: builder.mutation<SmsGraphResponse, SmsGraphRequestBody | void>({
      query: (body) => ({
        url: 'api/sms/report/graph',
        method: 'POST',
        body: body || {},
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useFilterChannelMutation,
  useGetChannelListMutation,
  useGetChannelDtlMutation,
  useGetChannelDeatilMutation,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useDeleteChannelsMutation,
  useSendIVRRequestMutation,
  useSendDIDRequestMutation,
  useAssignChannelMutation,
  useSyncMiscrosoftTeamsContactMutation,

  // BMS SMS API
  useLazyGetBmsBalanceQuery,
  useLazyGetBmsStatusQuery,
  useLazyGetBmsCoverageQuery,

  // SMS Usage & Report API (Tenant)
  useGetSmsUsageQuery,
  useLazyGetSmsUsageQuery,
  useGetSmsReportQuery,
  useLazyGetSmsReportQuery,
  useGetSmsReportGraphMutation,
} = channelApi;
